import { createContext, useContext } from 'react';
import type { DailyEngagementConfig } from '@/types';

export interface DraftContextValue {
  /** The document being edited (the served one until the admin changes it). */
  draft: DailyEngagementConfig;
  /** config:edit allowed, config not read-only, and the served document loaded. */
  editable: boolean;
  /** Client-side validation (the backend's messages) of the draft. */
  errors: string[];
  /** Apply a change to a deep copy of the draft. */
  update: (change: (draft: DailyEngagementConfig) => void) => void;
}

export const DraftContext = createContext<DraftContextValue | null>(null);

export function useDraft(): DraftContextValue {
  const value = useContext(DraftContext);
  if (!value) throw new Error('useDraft must be used inside the Daily Rewards page');
  return value;
}

/** An InputNumber's value as a document number: a cleared input is NaN (sent as null, refused). */
export const toNumber = (v: number | string | null | undefined): number =>
  typeof v === 'number' ? v : NaN;

/** A document number as an InputNumber value. */
export const fromNumber = (v: number): number | null => (Number.isFinite(v) ? v : null);

/** The daily_engagement_enabled flag as read from GET /admin/config/feature-flags. */
export type SwitchState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ok'; on: boolean };
